import React from 'react';
import PropTypes from 'prop-types';
import { ButtonInputClear } from 'components/buttons';
import { Close, Lock } from 'components/SvgIcons';
import CopyToClipboard from 'components/CopyToClipboard';
import { CFM_STONE, CFM_METAL } from 'constants/Colors';
import './styles.scss';

/**
 * Input Text Component
 */
class InputText extends React.PureComponent {
    constructor(props) {
        super(props);

        this.textInput = React.createRef();

        const { value = '' } = this.props;
        this.state = {
            focused: false,
            text: value || '',
        };
    }

    static getDerivedStateFromProps(props, state) {
        const { value } = props;
        const { text } = state;
        if (value !== text) {
            return {
                text: value,
            };
        }
        return null;
    }

    onFocus = (event) => {
        const { onFocus = () => null } = this.props;
        this.setState({ focused: true });
        onFocus(event);
    };

    onBlur = (event) => {
        const { onBlur = () => null } = this.props;
        this.setState({ focused: false });
        onBlur(event);
    };

    onChange = (e) => {
        const { onChange = () => null } = this.props;
        this.setState({ text: e.target.value });
        if (onChange) {
            onChange(e.target.value);
        }
    };

    onClearHandler = () => {
        this.clearInput();
        this.focusTextInput();
    };

    focusTextInput() {
        // Explicitly focus the text input using the raw DOM API
        // Note: we're accessing "current" to get the DOM node
        this.textInput.current.focus();
    }

    clearInput() {
        const { onClearClick = () => null } = this.props;
        this.setState({ text: '' }, () => {
            onClearClick();
        });
    }

    render() {
        const {
            className = '',
            placeholder = '',
            id = '',
            showIcon = true,
            readOnly,
            hasError,
            copiable,
            dataTest = '',
            ...rest
        } = this.props;

        const { text, focused } = this.state;

        const parentClassNames = ['input-text-wrapper'];
        if (focused) {
            parentClassNames.push('text-input--focus');
        }
        if (readOnly) {
            parentClassNames.push('input-text-readonly');
        }
        if (hasError) {
            parentClassNames.push('input-error');
        }
        if (showIcon) {
            parentClassNames.push('text-input__with-icon');
        }

        let classNames = ['text-input'];
        if (className) {
            classNames = classNames.concat(className);
        }

        return (
            <div className={parentClassNames.join(' ')}>
                <input
                    {...rest}
                    ref={this.textInput}
                    id={id}
                    type="text"
                    className={classNames.join(' ')}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    value={text}
                    onChange={this.onChange}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    data-test={dataTest}
                />
                <div className="input-text-icon">
                    {readOnly && (
                        <i className="input-text-icon__lock">
                            <Lock color={CFM_METAL} />
                        </i>
                    )}
                    {copiable && (
                        <div className="input-text-icon__copy-to-clipboard">
                            <CopyToClipboard color={CFM_METAL} value={text} />
                        </div>
                    )}
                    {showIcon && !readOnly && text.length > 0 && (
                        <ButtonInputClear onClick={this.onClearHandler} tabIndex="-1">
                            <i className="input-text-icon__lock">
                                <Close color={CFM_STONE} />
                            </i>
                        </ButtonInputClear>
                    )}
                </div>
            </div>
        );
    }
}

InputText.propTypes = {
    /** Description of prop "value". */
    value: PropTypes.string,
    /** Description of prop "placeholder". */
    placeholder: PropTypes.string,
    /** Disable input */
    disabled: PropTypes.bool,
    /** Class name property */
    className: PropTypes.string,
    /** input id */
    id: PropTypes.string,
    /** onchange */
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    showIcon: PropTypes.bool,
    onClearClick: PropTypes.func,
    hasError: PropTypes.bool,
    readOnly: PropTypes.bool,
    copiable: PropTypes.bool,
    dataTest: PropTypes.string,
};

export default InputText;
